import axios from 'axios';
import Toast from "light-toast";
import { API_BASE_URL, EXTERNAL_END_POINTS, COMMON_HEADERS_EXTERNAL_API } from "assets/constants/Constants";
import ShowToast from "components/ShowToast";
import { TYPE_FAIL, INTERVAL } from "assets/constants/Constants";
import qs from "qs";

const GET = "GET";
const POST = "POST";
const PUT = "PUT";
const DELETE = "DELETE";

let lastToastTimestamp = 0;

const showToastOnce = (message, duration, errorCode) => {
    const currentTimestamp = Date.now();

    // Check if enough time has passed since the last toast
    if (currentTimestamp - lastToastTimestamp >= duration) {
        lastToastTimestamp = currentTimestamp;
        if (errorCode === 403 || errorCode === 401) {
            Toast.fail(message, 3000, () => {
                localStorage.clear();
                window.location.href = "/login-page";
            });
        } else {
            ShowToast(message, TYPE_FAIL, INTERVAL, "", "");
        }
    }
};

const externalHttpHandler = async (url, method, additionalHeaders, body) => {
    const commonHeaders = { ...COMMON_HEADERS_EXTERNAL_API };
    if (url === EXTERNAL_END_POINTS.getToken) {
        commonHeaders['Content-Type'] = `application/x-www-form-urlencoded`;
    }
    else {
        commonHeaders['Authorization'] = `Bearer ${localStorage.getItem("externalAPIToken")}`;
    }

    const headers = Object.assign({}, commonHeaders, additionalHeaders);

    const axiosOptions = {
        method: method,
        headers: headers,
        data: commonHeaders['Content-Type'] !== COMMON_HEADERS_EXTERNAL_API['Content-Type'] ? qs.stringify(body) : body,
        url: `${process.env.REACT_APP_EXTERNAL_API_BASE_URL}/${url}`,
    };

    try {
        if (navigator.onLine) {
            const resp = await axios(axiosOptions);
            const response = { statusCode: resp.status, data: await resp.data };

            switch (response.statusCode) {
                case 200:
                    return Object.assign(response, { status: "SUCCESS" });
                case 500:
                    return Object.assign(response, { status: "WARNING", message: "Something went wrong. Please try again later." });
                default:
                    return response;
            }
        } else {
            // Handle internet connection error
            showToastOnce("No internet connection", INTERVAL);
            return { statusCode: 0, status: "ERROR", message: "No internet connection" };
        }

    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 403:
                    showToastOnce("Session expired. Redirecting to login page...", INTERVAL, 403);
                    break;

                case 401:
                    showToastOnce("Unauthorized. Redirecting to login page...", INTERVAL, 401);
                    break;

                case 404:
                    showToastOnce("Resource not found", INTERVAL, 404);
                    break;

                case 429:
                    showToastOnce("Too Many Requests. Please try again later.", INTERVAL, 429);
                    break;

                default:
                    showToastOnce("Network error", INTERVAL, null);
                    break;
            }
        } else if (error.request) {
            showToastOnce("Internal Server Error. Please try again later", INTERVAL, 500);
        } else {
            showToastOnce("Unexpected error", INTERVAL, null);
        }

        return { statusCode: 0, status: "ERROR", message: "Network error or other issues" };
    }
}

const httpHandler = async (url, method, additionalHeaders, body) => {
    const commonHeaders = {
        'Accept': '*/*',
        'Content-Type': 'application/json'
    };

    if (url !== "login") {
        commonHeaders['Authorization'] = `Bearer ${localStorage.getItem("JWToken")}`;
    }

    const headers = Object.assign({}, commonHeaders, additionalHeaders);

    const axiosOptions = {
        method: method,
        headers: headers,
        data: JSON.stringify(body),
        url: `${API_BASE_URL}/${url}`,
    };

    try {
        if (navigator.onLine) {
            const resp = await axios(axiosOptions);
            const response = { statusCode: resp.status, data: await resp.data };

            switch (response.statusCode) {
                case 200:
                    return Object.assign(response, { status: "SUCCESS" });
                case 500:
                    return Object.assign(response, { status: "WARNING", message: "Something went wrong. Please try again later." });
                default:
                    return response;
            }
        } else {
            // Handle internet connection error
            showToastOnce("No internet connection", INTERVAL);
            return { statusCode: 0, status: "ERROR", message: "No internet connection" };
        }

    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 403:
                    showToastOnce("Session expired. Redirecting to login page...", INTERVAL, 403);
                    break;

                case 401:
                    showToastOnce("Unauthorized. Redirecting to login page...", INTERVAL, 401);
                    break;

                case 404:
                    showToastOnce("Resource not found", INTERVAL, 404);
                    break;

                case 429:
                    showToastOnce("Too Many Requests. Please try again later.", INTERVAL, 429);
                    break;

                default:
                    showToastOnce("Network error", INTERVAL, null);
                    break;
            }
        } else if (error.request) {
            showToastOnce("Internal Server Error. Please try again later", INTERVAL, 500);
        } else {
            showToastOnce("Unexpected error", INTERVAL, null);
        }

        return { statusCode: 0, status: "ERROR", message: "Network error or other issues" };
    }
}

export const getHistoryList = async (additionalHeaders, body, event) => {
    const url = `auditlogs/list`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getUserHistoryList = async (additionalHeaders, body, userId) => {
    const url = `auditlogs/user-list/user/${userId}`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getDownloadList = async (additionalHeaders, body, event) => {
    const url = `auditlogs/downloadList`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getUserDownloadList = async (additionalHeaders, body, userId) => {
    const url = `auditlogs/downloadList/user/${userId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getUserCreditUsageList = async (additionalHeaders, body, userId) => {
    const url = `auditlogs/userCreditUsageList/user/${userId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const createAuditLogCall = async (additionalHeaders, body) => {
    const url = `auditlogs`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getReferralList = async (additionalHeaders, body, referralCode) => {
    const url = `user/referral-history/${referralCode}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const sendInviteCall = async (additionalHeaders, body) => {
    const url = `user/referral/code/send-invite`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const getSubscriptionList = async (additionalHeaders, body, event) => {
    const url = `subscription/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const createSubscriptionCall = async (additionalHeaders, body) => {
    const url = `subscription`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const updatePricingCall = async (additionalHeaders, body, pricingId) => {
    const url = `pricing/${pricingId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const createPricingCall = async (additionalHeaders, body) => {
    const url = `pricing`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getPricingList = async (additionalHeaders, body, event) => {
    const url = `pricing/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getRegionWiseCreditDetails = async (additionalHeaders, body, regionId, module) => {
    const url = `pricing/getCredit/${regionId}/${module}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const updateUserCredit = async (additionalHeaders, body, userId) => {
    const url = `user/updateCredit/${userId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}
export const deletePricingModelCall = async (additionalHeaders, body, pricingId) => {
    const url = `pricing/${pricingId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
}

export const initiateTransaction = async (additionalHeaders, body) => {
    const url = 'payment/initiate-transaction';
    return await httpHandler(url, POST, additionalHeaders, body);
}

export const updateTransaction = async (additionalHeaders, body) => {
    const url = 'payment/update-transaction';
    return await httpHandler(url, PUT, additionalHeaders, body);
}

export const getPaymentTranactionListAll = async (additionalHeaders, body) => {
    const url = `payment/transaction-history/all`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getUserPaymentTranactionList = async (additionalHeaders, body, userId) => {
    const url = `payment/transaction-history/user/${userId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const updateSubscriptionCall = async (additionalHeaders, body, subscriptionId) => {
    const url = `subscription/${subscriptionId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const deleteSubscriptionCall = async (additionalHeaders, body, subscriptionId) => {
    const url = `subscription/${subscriptionId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
}

export const forgotPasswordCall = async (additionalHeaders, body) => {
    const url = `user/forgetPassword`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getRoleCall = async (additionalHeaders, body, role) => {
    const url = `role/${role}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getUserRoleId = async (additionalHeaders, body, roleName) => {
    const url = `role/getRoleName/${roleName}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getMetaDataCall = async (additionalHeaders, body) => {
    const url = `getMetadata`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getRoleListCall = async (additionalHeaders, body) => {
    const url = `role`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const createRoleCall = async (additionalHeaders, body) => {
    const url = `role`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const updateRoleCall = async (additionalHeaders, body, roleId) => {
    const url = `role/${roleId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const deleteRole = async (additionalHeaders, body, roleId) => {
    const url = `role/${roleId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
}

export const findUserNameCall = async (additionalHeaders, body, event) => {
    const url = `user/findUserName/${event}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const getUserEmailId = async (additionalHeaders, body, userEmail) => {
    const url = `user/getUserEmail/${userEmail}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const checkUserReferralCode = async (additionalHeaders, body, referredCode) => {
    const url = `user/checkReferral/${referredCode}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const createUserCall = async (additionalHeaders, body) => {
    const url = `user`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const createNewUserAuthenticatedByGoogle = async (additionalHeaders, body) => {
    const url = 'user/create/google-auth';
    return await httpHandler(url, POST, additionalHeaders, body);
}

export const UpdateUserCall = async (additionalHeaders, body, userId) => {
    const url = `user/${userId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const getUserList = async (additionalHeaders, body, event) => {
    const url = `user`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const deleteUserCall = async (additionalHeaders, body, userId) => {
    const url = `user/${userId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
}

export const refreshAccessTokenCall = async (additionalHeaders, body) => {
    const url = `refreshToken`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const signInCall = async (additionalHeaders, body) => {
    const url = `login`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getPermissionMatrixCall = async (additionalHeaders, body) => {
    const url = `permissionmatrix`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const resetPasswordByOtpCall = async (additionalHeaders, body) => {
    const url = `user/resetpassword/otp`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const resetPasswordByAdminCall = async (additionalHeaders, body) => {
    const url = `user/reset/password/by/admin`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
}

export const getUserDetails = async (additionalHeaders, body, userId) => {
    const url = `user/${userId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const createUserEnquiryCall = async (additionalHeaders, body) => {
    const url = `user/contact/enquiry`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const getS3PresignedUrl = async (additionalHeaders, body) => {
    const url = `generate-presigned-url`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const uploadDocumentBucket = async (additionalHeaders, body) => {
    const { url, file } = body;
    const response = await axios.put(url, file, { headers: additionalHeaders });
    return response;
};

export const downloadDocumentBucket = async (additionalHeaders, body) => {
    const url = `generate-download-url`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const maiGetToken = async (username = '', password = '') => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await axios.post(`{MILLION_AI_HOST}/api/v1/auth/token`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response;
};

export const maiSearch = async () => { };

export const maiSingleCollect = async () => { };

export const getLoginUserTokenForExternalAPICall = async (additionalHeaders, body) => {
    const url = EXTERNAL_END_POINTS.getToken;
    const response = await externalHttpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const apiCollectExternalCall = async (additionalHeaders, body) => {
    const url = EXTERNAL_END_POINTS.apiCollect;
    const response = await externalHttpHandler(url, POST, additionalHeaders, body);
    return response;
}

export const apiSearchCompanyExternalCall = async (additionalHeaders, body, domain = "", l1_process = "", l2_process = "", keyword = "") => {

    const params = new URLSearchParams();

    if (domain) params.append("domain", domain);
    if (l1_process) params.append("l1_process", l1_process);
    if (l2_process) params.append("l2_process", l2_process);
    if (keyword) params.append("keyword", keyword);

    // Always add pagination defaults
    params.append("page_size", 50);
    params.append("page_number", 1);

    const url = `${EXTERNAL_END_POINTS.searchCompany}/?${params.toString()}`;
    const response = await externalHttpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const apiGetCompanyDetailExternalCall = async (additionalHeaders, body, companyId) => {
    const url = `${EXTERNAL_END_POINTS.getCompany}/${companyId}`;
    const response = await externalHttpHandler(url, GET, additionalHeaders, body);
    return response;
}

export const apiGetCompanyScoringResults = async (additionalHeaders, body, companyId) => {
    const url = `${EXTERNAL_END_POINTS.getCompanyScore}/${companyId}`;
    const response = await externalHttpHandler(url, GET, additionalHeaders, body);
    return response;
};